/**
 * SLIDE SHOWS — the page runtime's part of `style-system/slides-effect.ts`
 * (job 8 in page-runtime.ts).
 *
 * An outer box carries `data-pb-slides` and its settings. Inside it, the
 * designer's own elements may be marked `data-pb-slides-role`: `slides` (the
 * box whose items are the slides; unmarked, the outer box's own items are),
 * `prev` / `next` (a press steps the strip), `dots` (a box with ONE template
 * dot, repeated here once per slide; the current one gets `aria-current`).
 *
 * The strip is a real scroll container with snap (the CSS), so a swipe works
 * without this script and the scroll position is the single truth: a step is
 * an animated scroll, and a swipe updates the dots through the same scroll
 * listener. Snapping is switched off while a step animates (a snap container
 * re-snaps after every programmatic scroll and would fight the animation) and
 * restored on arrival.
 *
 * Wrap round: a copy of the first slide (`data-pb-slide-clone`, hidden from
 * assistive tech, inert) sits after the last, and a copy of the last before
 * the first — so a centred strip shows a neighbour on both sides of every
 * slide, the first included. Next from the last slide moves onto the end
 * copy, then jumps home instantly (the two look the same, so the jump is
 * invisible); Previous from the first moves onto the start copy and jumps to
 * the last the same way.
 *
 * Where a slide lands follows its computed `scroll-snap-align`: the strip's
 * own Snap (Start or Centre) is the truth, and a step scrolls to the same
 * place a swipe would snap to.
 *
 * It pauses off screen, while the tab is hidden, on hover (unless told to keep
 * going), while a part has focus, and while a finger is on the strip. For
 * anyone who prefers reduced motion it never plays by itself and every step
 * is instant.
 *
 * Up and down (`data-pb-slides-dir="vertical"`): the same show on the y axis
 * — the strip is a column (the CSS), steps scroll `scrollTop`, the wrap
 * copies sit above and below.
 *
 * Every slide carries `data-pb-slide="in" | "before" | "after"` for its place
 * against the one in view, and the wrap copies too; the slides-not-in-view
 * CSS (slides-effect.ts) writes the neighbours' look on before and after. The
 * mark moves as a step begins, so the look plays with the step.
 *
 * A centred strip whose slides are narrower than it (As sized, Snap Centre)
 * gets a SPACER at each end (`data-pb-slide-spacer`), sized so the first and
 * last slide — and the wrap copies beyond them — can reach the centre. The
 * browser clamps the scroll at both ends, so without the room the first few
 * and last few slides shared one position and the wrap stalled, then jumped
 * (John, 2026-10-21: "you have this set to loop yet it does not loop").
 *
 * A RING (`data-pb-slides-shape="ring"`): the slides stand on a cylinder and
 * the stage turns instead of scrolling. This writes the ring's variables
 * (slide number, step angle, radius, turn — `RING_VARS` in slides-effect.ts)
 * and the CSS draws it. A step turns the stage one slide; it always goes
 * round, so there are no wrap copies; a drag or a sideways trackpad swipe
 * turns it by hand, one slide's width per slide; ← → on the stage step it. The radius is the given one,
 * or the one that puts the slides shoulder to shoulder.
 */

interface Show {
  root: HTMLElement;
  scroller: HTMLElement;
  slides: HTMLElement[];
  /** The wrap's copies: of the first slide after the last, of the last before the first. */
  clone: HTMLElement | null;
  lead: HTMLElement | null;
  dots: HTMLElement[];
  index: number;
  timer: number | null;
  animating: boolean;
  inView: boolean;
  hovered: boolean;
  focused: boolean;
  pressed: boolean;
  /** Stopped by the visitor — the pause button, or a tap on a touch screen —
   *  until the pause button is pressed again. */
  userPaused: boolean;
  /** A mouse is dragging a line: the strip follows the pointer, not a swipe to settle. */
  dragging: boolean;
  settled: number | null;
  /** Up and down. */
  vertical: boolean;
  /** The slides stand on a cylinder that turns. */
  ring: boolean;
  /** The ring's turn in degrees, continuous (a step is `-step`). */
  turn: number;
  /** The ring's angle between slides. */
  step: number;
}

const ROLE = 'data-pb-slides-role';
/** When a part shows: `hover` (a pointer over the show, or focus inside it),
 *  `focus` (only while it has keyboard focus). The CSS reads it. */
const REVEAL = 'data-pb-slides-reveal';
/** Playing or paused, on the show, for the pause button's two faces. */
const STATE = 'data-pb-slides-state';
/** Controls the runtime makes when the designer gave none. */
const MADE = 'data-pb-slides-made';
const CLONE = 'data-pb-slide-clone';
const PLACE = 'data-pb-slide';
/** The room at a centred strip's ends (runtime-made). */
const SPACER = 'data-pb-slide-spacer';
/** The ring's variables — the same names as RING_VARS in slides-effect.ts
 *  (not imported: this file is built into the page runtime on its own). */
const RING = { index: '--pb-ring-i', step: '--pb-ring-step', radius: '--pb-ring-r', turn: '--pb-ring-turn' };

const num = (v: string | null, fallback: number) => {
  const n = Number(v);
  return v !== null && v !== '' && Number.isFinite(n) ? n : fallback;
};

// ── Easing: the CSS the Transition editor wrote, evaluated here ─────────────
// Keywords, `cubic-bezier(x1, y1, x2, y2)` and `linear(stop, stop, …)` (what a
// spring compiles to, transition-spec.ts). Anything else plays as `ease`.
type Ease = (t: number) => number;
const KEYWORDS: Record<string, [number, number, number, number]> = {
  linear: [0, 0, 1, 1],
  ease: [0.25, 0.1, 0.25, 1],
  'ease-in': [0.42, 0, 1, 1],
  'ease-out': [0, 0, 0.58, 1],
  'ease-in-out': [0.42, 0, 0.58, 1],
};

function bezier(x1: number, y1: number, x2: number, y2: number): Ease {
  const ax = 1 - 3 * x2 + 3 * x1, bx = 3 * x2 - 6 * x1, cx = 3 * x1;
  const ay = 1 - 3 * y2 + 3 * y1, by = 3 * y2 - 6 * y1, cy = 3 * y1;
  const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number) => ((ay * t + by) * t + cy) * t;
  const slopeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  return (x: number) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const dx = sampleX(t) - x;
      if (Math.abs(dx) < 1e-5) break;
      const d = slopeX(t);
      if (Math.abs(d) < 1e-6) break;
      t -= dx / d;
    }
    return sampleY(Math.max(0, Math.min(1, t)));
  };
}

function linearStops(body: string): Ease | null {
  // `linear(0, 0.3 20%, 1)`: each stop is an output, with an optional input
  // percentage; missing inputs spread evenly between the known ones.
  const raw = body.split(',').map((s) => s.trim()).filter(Boolean);
  if (raw.length < 2) return null;
  const stops: Array<{ y: number; x: number | null }> = raw.map((s) => {
    const parts = s.split(/\s+/);
    const y = Number(parts[0]);
    const x = parts[1] && parts[1].endsWith('%') ? Number(parts[1].slice(0, -1)) / 100 : null;
    return { y: Number.isFinite(y) ? y : 0, x };
  });
  stops[0].x = stops[0].x ?? 0;
  stops[stops.length - 1].x = stops[stops.length - 1].x ?? 1;
  for (let i = 1; i < stops.length; i++) {
    if (stops[i].x !== null) continue;
    let j = i + 1;
    while (stops[j].x === null) j++;
    const x0 = stops[i - 1].x as number, x1 = stops[j].x as number;
    for (let k = i; k < j; k++) stops[k].x = x0 + ((x1 - x0) * (k - i + 1)) / (j - i + 1);
  }
  const pts = stops as Array<{ y: number; x: number }>;
  return (x: number) => {
    if (x <= 0) return pts[0].y;
    if (x >= 1) return pts[pts.length - 1].y;
    for (let i = 1; i < pts.length; i++) {
      if (x <= pts[i].x) {
        const a = pts[i - 1], b = pts[i];
        const span = b.x - a.x;
        return span <= 0 ? b.y : a.y + ((b.y - a.y) * (x - a.x)) / span;
      }
    }
    return 1;
  };
}

export function easingFor(css: string | null): Ease {
  const s = (css || '').trim();
  if (KEYWORDS[s]) return bezier(...KEYWORDS[s]);
  const cb = /^cubic-bezier\(([^)]+)\)$/.exec(s);
  if (cb) {
    const n = cb[1].split(',').map((v) => Number(v.trim()));
    if (n.length === 4 && n.every((v) => Number.isFinite(v))) return bezier(n[0], n[1], n[2], n[3]);
  }
  const lin = /^linear\((.+)\)$/.exec(s);
  if (lin) {
    const fn = linearStops(lin[1]);
    if (fn) return fn;
  }
  return bezier(...KEYWORDS.ease);
}

// ── One show ────────────────────────────────────────────────────────────────

const shows = new WeakMap<Element, Show>();

/** The element whose children are the slides: the marked Slides box's inner div, or the outer box's. */
function findScroller(root: HTMLElement): HTMLElement | null {
  const marked = Array.from(root.querySelectorAll<HTMLElement>(`[${ROLE}="slides"]`)).find((el) => el.closest('[data-pb-slides]') === root);
  const strip = marked ?? root;
  // A Box renders its items inside one inner <div>; the first div child that
  // has children is it (a section's empty effect layers come first).
  const inner = Array.from(strip.children).find((c) => c.tagName === 'DIV' && c.children.length > 0) as HTMLElement | undefined;
  return inner ?? null;
}

function partsOf(root: HTMLElement, role: string): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(`[${ROLE}="${role}"]`)).filter((el) => el.closest('[data-pb-slides]') === root);
}

function makePressable(el: HTMLElement, label: string, press: () => void) {
  const tag = el.tagName;
  const isButton = tag === 'BUTTON' || tag === 'A' || el.getAttribute('role') === 'button';
  if (!isButton) {
    el.setAttribute('role', 'button');
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
  }
  if (!el.hasAttribute('aria-label') && !el.textContent?.trim()) el.setAttribute('aria-label', label);
  el.addEventListener('click', (e) => {
    if (tag === 'A') e.preventDefault();
    press();
  });
  if (!isButton) {
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); press(); }
    });
  }
}

/**
 * The scroll position that lands `el` where its snap says: its start at the
 * scroller's padded start edge, or its centre in the middle. Measured from
 * rectangles, so it holds whatever the strip's padding or offset parent.
 */
function targetFor(show: Show, el: HTMLElement, win: Window): number {
  const { scroller, vertical } = show;
  const sr = scroller.getBoundingClientRect();
  const er = el.getBoundingClientRect();
  const align = win.getComputedStyle(el).scrollSnapAlign;
  const centred = /center/.test(align);
  const atEnd = /end/.test(align);
  const scs = win.getComputedStyle(scroller);
  if (vertical) {
    const pad = parseFloat(scs.scrollPaddingBlockStart) || 0;
    const offset = centred ? (scroller.clientHeight - er.height) / 2 : atEnd ? scroller.clientHeight - er.height : pad;
    return Math.max(0, scroller.scrollTop + (er.top - sr.top) - offset);
  }
  const pad = parseFloat(scs.scrollPaddingInlineStart) || 0;
  const offset = centred ? (scroller.clientWidth - er.width) / 2 : atEnd ? scroller.clientWidth - er.width : pad;
  return Math.max(0, scroller.scrollLeft + (er.left - sr.left) - offset);
}

/** The strip's position on the show's axis: its scroll, or the ring's turn. */
function posOf(show: Show): number {
  if (show.ring) return show.turn;
  return show.vertical ? show.scroller.scrollTop : show.scroller.scrollLeft;
}
function setPos(show: Show, v: number): void {
  if (show.ring) {
    show.turn = v;
    show.scroller.style.setProperty(RING.turn, `${v}deg`);
    return;
  }
  if (show.vertical) show.scroller.scrollTop = v;
  else show.scroller.scrollLeft = v;
}

/**
 * Mark every slide's place against `current` (a slide or a wrap copy): the
 * ones before it in the strip `before`, the ones after it `after`. On a ring
 * the sides are angular: the slides round the near half after the current one
 * are `after`, the rest `before`, as on a line that joins up.
 */
function paintPlaces(show: Show, current: HTMLElement): void {
  if (show.ring) {
    const n = show.slides.length;
    const cur = show.slides.indexOf(current);
    show.slides.forEach((el, i) => {
      const d = (((i - cur) % n) + n) % n;
      el.setAttribute(PLACE, d === 0 ? 'in' : d <= n / 2 ? 'after' : 'before');
    });
    return;
  }
  let side: 'before' | 'in' | 'after' = 'before';
  for (const el of Array.from(show.scroller.children)) {
    if (el.hasAttribute(SPACER)) continue;
    if (el === current) {
      el.setAttribute(PLACE, 'in');
      side = 'after';
      continue;
    }
    el.setAttribute(PLACE, side);
  }
}

/** The radius that puts `n` slides of width `w` with `gap` between them shoulder to shoulder (slides-effect.ts has the same). */
function ringRadiusFor(n: number, w: number, gap: number): number {
  if (n < 2) return 0;
  return Math.round(((w + gap) / (2 * Math.tan(Math.PI / n))) * 10) / 10;
}

/**
 * Lay the ring out: each slide's number, the step angle, and the radius —
 * the given one, or the one measured from the first slide's width and the
 * stage's gap. Run on bind and on resize.
 */
function layRing(show: Show, win: Window): void {
  const { scroller, slides } = show;
  const n = slides.length;
  show.step = 360 / n;
  slides.forEach((el, i) => el.style.setProperty(RING.index, String(i)));
  const given = num(show.root.getAttribute('data-pb-slides-ring'), 0);
  const scs = win.getComputedStyle(scroller);
  const gap = parseFloat(show.vertical ? scs.rowGap : scs.columnGap) || 0;
  const radius = given > 0 ? given : ringRadiusFor(n, show.vertical ? slides[0].offsetHeight : slides[0].offsetWidth, gap);
  scroller.style.setProperty(RING.step, `${show.step}deg`);
  scroller.style.setProperty(RING.radius, `${radius}px`);
  setPos(show, show.turn);
}

/**
 * The room a centred strip needs at each end so its first and last items can
 * reach the centre: a spacer of `(strip − item) / 2` less the padding and gap
 * already there. Nothing on a strip whose items snap to the start, or are as
 * wide as it. Rebuilt on resize.
 */
function fitEnds(show: Show, win: Window): void {
  const { scroller, vertical } = show;
  scroller.querySelectorAll(`[${SPACER}]`).forEach((el) => el.remove());
  const items = Array.from(scroller.children) as HTMLElement[];
  if (items.length === 0) return;
  const first = items[0], last = items[items.length - 1];
  if (!/center/.test(win.getComputedStyle(first).scrollSnapAlign)) return;
  const scs = win.getComputedStyle(scroller);
  const size = (el: HTMLElement) => (vertical ? el.offsetHeight : el.offsetWidth);
  const inner = vertical ? scroller.clientHeight : scroller.clientWidth;
  const gap = parseFloat(vertical ? scs.rowGap : scs.columnGap) || 0;
  const padStart = parseFloat(vertical ? scs.paddingTop : scs.paddingLeft) || 0;
  const padEnd = parseFloat(vertical ? scs.paddingBottom : scs.paddingRight) || 0;
  const spacer = (px: number) => {
    const el = scroller.ownerDocument.createElement('div');
    el.setAttribute(SPACER, '');
    el.setAttribute('aria-hidden', 'true');
    el.style.flex = `0 0 ${px}px`;
    el.style[vertical ? 'height' : 'width'] = `${px}px`;
    el.style.pointerEvents = 'none';
    return el;
  };
  const needStart = (inner - size(first)) / 2 - padStart - gap;
  const needEnd = (inner - size(last)) / 2 - padEnd - gap;
  if (needStart > 0.5) scroller.insertBefore(spacer(needStart), first);
  if (needEnd > 0.5) scroller.appendChild(spacer(needEnd));
}

export function bindSlideShow(root: HTMLElement, win: Window): Show | null {
  if (shows.has(root)) return shows.get(root) ?? null;
  const scroller = findScroller(root);
  if (!scroller) return null;
  const slides = Array.from(scroller.children).filter((c) => !c.hasAttribute(CLONE) && !c.hasAttribute(SPACER)) as HTMLElement[];
  if (slides.length < 2) return null;
  const doc = root.ownerDocument;
  const reduced = !!win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const auto = root.getAttribute('data-pb-slides') === 'auto' && !reduced;
  const everyMs = Math.max(500, num(root.getAttribute('data-pb-slides-every'), 5000));
  const movesMs = reduced ? 0 : Math.max(0, num(root.getAttribute('data-pb-slides-moves'), 600));
  const ease = easingFor(root.getAttribute('data-pb-slides-easing'));
  const ring = root.getAttribute('data-pb-slides-shape') === 'ring';
  // A ring always goes round, and only sideways.
  const wrap = !ring && root.getAttribute('data-pb-slides-wrap') !== 'off';
  const pauseOnHover = root.getAttribute('data-pb-slides-hover') !== 'play';
  // Up and down: a column on a line; on a ring, a drum round the horizontal
  // axis — the next slide below, turned by a drag up and down.
  const vertical = root.getAttribute('data-pb-slides-dir') === 'vertical';

  const show: Show = { root, scroller, slides, clone: null, lead: null, dots: [], index: 0, timer: null, animating: false, inView: true, hovered: false, focused: false, pressed: false, userPaused: false, dragging: false, settled: null, vertical, ring, turn: 0, step: 360 / slides.length };
  shows.set(root, show);

  // Names for assistive tech: the region, and each slide's place in it.
  if (!root.hasAttribute('aria-roledescription')) root.setAttribute('aria-roledescription', 'carousel');
  slides.forEach((el, i) => {
    if (!el.hasAttribute('role')) el.setAttribute('role', 'group');
    if (!el.hasAttribute('aria-roledescription')) el.setAttribute('aria-roledescription', 'slide');
    if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', `${i + 1} of ${slides.length}`);
  });

  // Wrap round: the copy of the first slide after the last, and of the last
  // before the first.
  const copyOf = (el: HTMLElement) => {
    const clone = el.cloneNode(true) as HTMLElement;
    clone.setAttribute(CLONE, '');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.removeAttribute('aria-label');
    // No duplicate ids on the page.
    clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
    return clone;
  };
  if (wrap) {
    show.clone = copyOf(slides[0]);
    scroller.appendChild(show.clone);
    show.lead = copyOf(slides[slides.length - 1]);
    scroller.insertBefore(show.lead, scroller.firstChild);
  }
  if (ring) layRing(show, win);
  else fitEnds(show, win);

  const n = slides.length;
  /** Where `el` is: its scroll target on a line, or on a ring the turn that brings it to the front — the nearest one to where the ring is now. */
  const at = (el: HTMLElement) => {
    if (!ring) return targetFor(show, el, win);
    const i = slides.indexOf(el);
    const want = -i * show.step;
    const k = Math.round((show.turn - want) / 360);
    return want + k * 360;
  };
  const jumpTo = (el: HTMLElement) => {
    // A copy and its slide look the same: nobody sees this. The places are
    // marked with no transition, or the neighbours would be seen changing.
    const prev = scroller.style.scrollBehavior;
    scroller.style.scrollBehavior = 'auto';
    setPos(show, at(el));
    scroller.style.scrollBehavior = prev;
    const kids = Array.from(scroller.children) as HTMLElement[];
    const held = kids.map((k) => k.style.transition);
    kids.forEach((k) => (k.style.transition = 'none'));
    paintPlaces(show, el);
    // Flush, then let the transitions back.
    void scroller.offsetWidth;
    kids.forEach((k, i) => (k.style.transition = held[i]));
  };
  // Start on the first slide, past the lead copy.
  if (show.lead) jumpTo(slides[0]);
  else paintPlaces(show, slides[0]);
  const paintDots = () => {
    show.dots.forEach((d, i) => {
      if (i === show.index) d.setAttribute('aria-current', 'true');
      else d.removeAttribute('aria-current');
    });
  };

  // The strip's snapping is `!important` (slides-effect.ts, so the canvas
  // forwarding cannot undo it), so switching it off for a step has to be too
  // — a plain inline value loses, every frame re-snaps, and the step shows as
  // a cut instead of a slide (John, 2026-10-20: "they don't slide over, they
  // just change").
  const setSnap = (on: boolean) => {
    if (ring) return;
    if (on) scroller.style.removeProperty('scroll-snap-type');
    else scroller.style.setProperty('scroll-snap-type', 'none', 'important');
  };

  let frame = 0;
  const cancelMove = () => {
    if (frame) win.cancelAnimationFrame(frame);
    frame = 0;
    show.animating = false;
    setSnap(true);
  };

  /** Scroll to `left` over the step's time, then `then`. */
  const moveTo = (left: number, then?: () => void) => {
    cancelMove();
    const from = posOf(show);
    const delta = left - from;
    if (movesMs <= 0 || Math.abs(delta) < 1) {
      setPos(show, left);
      then?.();
      return;
    }
    show.animating = true;
    setSnap(false);
    const start = win.performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / movesMs);
      setPos(show, from + delta * ease(t));
      if (t < 1) {
        frame = win.requestAnimationFrame(tick);
      } else {
        frame = 0;
        show.animating = false;
        setSnap(true);
        then?.();
      }
    };
    frame = win.requestAnimationFrame(tick);
  };

  const goTo = (i: number, resetTimer = true) => {
    if (resetTimer) schedule();
    if (ring) {
      // Turn by the difference, so Next from the last slide carries on round
      // rather than turning all the way back; a dot several away turns the
      // short way.
      const delta = i - show.index;
      const forward = ((delta % n) + n) % n;
      const steps = Math.abs(delta) === 1 ? delta : forward <= n / 2 ? forward : forward - n;
      show.index = ((i % n) + n) % n;
      paintDots();
      paintPlaces(show, slides[show.index]);
      moveTo(show.turn - steps * show.step);
      return;
    }
    if (i >= n) {
      show.index = 0;
      paintDots();
      if (wrap && show.clone) {
        paintPlaces(show, show.clone);
        moveTo(at(show.clone), () => jumpTo(slides[0]));
      } else {
        paintPlaces(show, slides[0]);
        moveTo(at(slides[0]));
      }
      return;
    }
    if (i < 0) {
      show.index = n - 1;
      paintDots();
      if (wrap && show.lead) {
        paintPlaces(show, show.lead);
        moveTo(at(show.lead), () => jumpTo(slides[n - 1]));
      } else {
        paintPlaces(show, slides[n - 1]);
        moveTo(at(slides[n - 1]));
      }
      return;
    }
    show.index = i;
    paintDots();
    paintPlaces(show, slides[i]);
    moveTo(at(slides[i]));
  };

  // The parts.
  partsOf(root, 'dots').forEach((box) => {
    const holder = (Array.from(box.children).find((c) => c.tagName === 'DIV') as HTMLElement | undefined) ?? box;
    const template = holder.firstElementChild as HTMLElement | null;
    if (!template) return;
    if (!holder.hasAttribute('role')) holder.setAttribute('role', 'group');
    if (!holder.hasAttribute('aria-label')) holder.setAttribute('aria-label', 'Choose a slide');
    holder.replaceChildren();
    for (let i = 0; i < n; i++) {
      const dot = template.cloneNode(true) as HTMLElement;
      dot.removeAttribute('id');
      dot.querySelectorAll('[id]').forEach((x) => x.removeAttribute('id'));
      dot.setAttribute('aria-label', `Slide ${i + 1} of ${n}`);
      makePressable(dot, `Slide ${i + 1} of ${n}`, () => goTo(i));
      holder.appendChild(dot);
      show.dots.push(dot);
    }
  });
  paintDots();

  // Every show can be moved by hand, whatever the designer added. With no
  // arrows of its own and no dots in view, a plain pair is made here; they
  // show on hover or keyboard focus (the CSS), never on touch, where a swipe
  // moves it. Dots that a width hides count as none at that width.
  const made = (role: string, reveal: string, label: string, face: string) => {
    const b = doc.createElement('button');
    b.type = 'button';
    b.setAttribute(ROLE, role);
    b.setAttribute(REVEAL, reveal);
    b.setAttribute(MADE, '');
    b.setAttribute('aria-label', label);
    b.innerHTML = face;
    if (win.getComputedStyle(root).position === 'static') root.style.position = 'relative';
    root.appendChild(b);
    return b;
  };
  const madeArrows: HTMLElement[] = [];
  if (!partsOf(root, 'prev').length && !partsOf(root, 'next').length) {
    const back = vertical ? 'M2 9l5-5 5 5' : 'M9 2L4 7l5 5';
    const on = vertical ? 'M2 5l5 5 5-5' : 'M5 2l5 5-5 5';
    const icon = (d: string) => `<svg viewBox="0 0 14 14" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="${d}"/></svg>`;
    madeArrows.push(made('prev', 'hover', 'Previous slide', icon(back)), made('next', 'hover', 'Next slide', icon(on)));
  }
  const fitArrows = () => {
    const dotsInView = partsOf(root, 'dots').some((d) => d.getClientRects().length > 0);
    madeArrows.forEach((b) => { b.hidden = dotsInView; });
  };
  fitArrows();
  partsOf(root, 'prev').forEach((el) => makePressable(el, 'Previous slide', () => goTo(show.index - 1)));
  partsOf(root, 'next').forEach((el) => makePressable(el, 'Next slide', () => goTo(show.index + 1)));

  // The pause button: stops a show that plays by itself until pressed again
  // (WCAG 2.2.2). Every such show has one — the designer's, or a plain one
  // made here that shows only while it has keyboard focus. With reduced
  // motion nothing plays, so the CSS hides it.
  if (auto && !partsOf(root, 'pause').length) {
    made('pause', 'focus', 'Pause slide show',
      '<svg data-pb-slides-when="playing" viewBox="0 0 14 14" width="14" height="14" fill="currentColor" aria-hidden="true"><rect x="3" y="2" width="3" height="10" rx="1"/><rect x="8" y="2" width="3" height="10" rx="1"/></svg>'
      + '<svg data-pb-slides-when="paused" viewBox="0 0 14 14" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M4 2v10l8-5z"/></svg>');
  }
  const pauses = partsOf(root, 'pause');
  const paintPause = () => {
    root.setAttribute(STATE, show.userPaused ? 'paused' : 'playing');
    for (const el of pauses) {
      el.setAttribute('aria-pressed', String(show.userPaused));
      el.setAttribute('aria-label', show.userPaused ? 'Play slide show' : 'Pause slide show');
    }
  };
  pauses.forEach((el) => {
    el.querySelectorAll('[data-pb-slides-when]').forEach((f) => f.setAttribute('aria-hidden', 'true'));
    makePressable(el, 'Pause slide show', () => { show.userPaused = !show.userPaused; paintPause(); schedule(); });
  });
  paintPause();

  // A swipe (or a keyboard scroll) moves the index with the strip.
  const settle = () => {
    show.settled = null;
    if (show.animating) return;
    if (ring) {
      const nearest = Math.round(-show.turn / show.step);
      show.index = ((nearest % n) + n) % n;
      paintDots();
      paintPlaces(show, slides[show.index]);
      moveTo(-nearest * show.step);
      return;
    }
    const left = posOf(show);
    let best = 0;
    let bestDist = Infinity;
    slides.forEach((el, i) => {
      const d = Math.abs(at(el) - left);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    // Swiped onto a copy: land on the slide it copies.
    let jumped = false;
    if (show.clone && Math.abs(at(show.clone) - left) < bestDist) { bestDist = Math.abs(at(show.clone) - left); best = 0; jumpTo(slides[0]); jumped = true; }
    if (show.lead && Math.abs(at(show.lead) - left) < bestDist) { best = n - 1; jumpTo(slides[n - 1]); jumped = true; }
    if (best !== show.index) {
      show.index = best;
      paintDots();
      if (!jumped) paintPlaces(show, slides[best]);
    }
  };
  scroller.addEventListener('scroll', () => {
    if (show.animating || show.dragging) return;
    schedule();
    if (show.settled !== null) win.clearTimeout(show.settled);
    show.settled = win.setTimeout(settle, 120);
  }, { passive: true });

  // A ring is turned by hand: a drag turns it one slide per slide's width,
  // and settles on the nearest; ← → step it from the keyboard (a line is a
  // scroll container, which the keyboard already moves).
  if (ring) {
    // The pointer and the wheel are taken on the strip's own box, not the
    // stage inside it. The front slide stands at depth 0 — the box's plane —
    // and once the stage has turned, rounding can leave it a hair behind, so
    // the browser hits the box: a swipe there never reached the stage, was not
    // prevented, and scrolled whatever was next (John, 2026-10-22: "only when
    // swiping on my Mac pad it stops"). The box holds the stage, so the slides
    // still reach it.
    const surface = (scroller.parentElement ?? scroller) as HTMLElement;
    let drag: { x: number; y: number; turn: number; moved: boolean } | null = null;
    // A slide's size along the drag: its width across a ring, its height on a drum.
    const widthOf = () => (vertical ? slides[0].offsetHeight : slides[0].offsetWidth) || 200;
    surface.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      cancelMove();
      drag = { x: e.clientX, y: e.clientY, turn: show.turn, moved: false };
    });
    surface.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const dx = vertical ? e.clientY - drag.y : e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) < 4) return;
      // Captured only once it is a drag: a press that stays put is a click,
      // and capturing it would send the click to the box, not the arrow,
      // dot or link it was on.
      if (!drag.moved && typeof surface.setPointerCapture === 'function') surface.setPointerCapture(e.pointerId);
      drag.moved = true;
      e.preventDefault();
      setPos(show, drag.turn + (dx / widthOf()) * show.step);
    });
    const end = (e: PointerEvent) => {
      if (!drag) return;
      const moved = drag.moved;
      drag = null;
      if (typeof surface.hasPointerCapture === 'function' && surface.hasPointerCapture(e.pointerId)) surface.releasePointerCapture(e.pointerId);
      if (moved) { schedule(); settle(); }
    };
    surface.addEventListener('pointerup', end);
    surface.addEventListener('pointercancel', end);
    // A card is a picture in a link: a few pixels in, the browser would start
    // dragging the picture itself, the pointer is cancelled and the turn
    // stops short — and every try after it does the same (John, 2026-10-21:
    // "it turns then stops, then I can't even turn it back again").
    surface.addEventListener('dragstart', (e) => e.preventDefault());
    // A drag is not a click on the slide it ended over.
    let swallow = false;
    surface.addEventListener('pointerup', () => { swallow = !!drag?.moved; }, true);
    surface.addEventListener('click', (e) => { if (swallow) { swallow = false; e.preventDefault(); e.stopPropagation(); } }, true);
    // A trackpad swipe is wheel events, not a pointer drag (a line's strip
    // scrolls on them by itself): sideways wheel turns the ring, and it
    // settles once the swipe stops. Up-and-down wheel is the page's.
    let wheelSettle: number | null = null;
    surface.addEventListener('wheel', (e) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      cancelMove();
      setPos(show, show.turn - (e.deltaX / widthOf()) * show.step);
      schedule();
      if (wheelSettle !== null) win.clearTimeout(wheelSettle);
      wheelSettle = win.setTimeout(() => { wheelSettle = null; settle(); }, 120);
    }, { passive: false });
    // Focus sits on the flat box too, never on the stage: the stage turns, and
    // the browser draws its focus outline turned with it — a long slanted
    // frame across the page (John, 2026-10-23: "a long blue line showing in
    // the ring carousel").
    if (!surface.hasAttribute('tabindex')) surface.setAttribute('tabindex', '0');
    const nextKey = vertical ? 'ArrowDown' : 'ArrowRight';
    const prevKey = vertical ? 'ArrowUp' : 'ArrowLeft';
    surface.addEventListener('keydown', (e) => {
      if (e.key === nextKey) { e.preventDefault(); goTo(show.index + 1); }
      else if (e.key === prevKey) { e.preventDefault(); goTo(show.index - 1); }
    });
  } else {
    if (!scroller.hasAttribute('tabindex') && !scroller.querySelector('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])')) {
      // A line is a scroll container, but only some browsers let the keyboard
      // reach one by itself (Safari does not). With nothing focusable inside —
      // a strip of pictures — it becomes the tab stop so the arrows scroll it.
      // With links inside, those are the stops and nothing is added.
      scroller.setAttribute('tabindex', '0');
    }
    // A mouse with no trackpad has no sideways scroll: a drag moves the line,
    // free under the pointer, and lands on the nearest slide when let go.
    // Touch scrolls the strip natively and is left alone.
    let drag: { at: number; pos: number; moved: boolean; from: number; d: number } | null = null;
    let swallow = false;
    const along = (e: PointerEvent) => (vertical ? e.clientY : e.clientX);
    scroller.addEventListener('pointerdown', (e) => {
      swallow = false;
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      drag = { at: along(e), pos: vertical ? scroller.scrollTop : scroller.scrollLeft, moved: false, from: show.index, d: 0 };
    });
    scroller.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const d = along(e) - drag.at;
      if (!drag.moved && Math.abs(d) < 4) return;
      drag.d = d;
      if (!drag.moved) {
        drag.moved = true;
        show.dragging = true;
        cancelMove();
        setSnap(false);
        scroller.style.scrollBehavior = 'auto';
        if (typeof scroller.setPointerCapture === 'function') scroller.setPointerCapture(e.pointerId);
      }
      if (vertical) scroller.scrollTop = drag.pos - d;
      else scroller.scrollLeft = drag.pos - d;
    });
    // Let go: glide onto the nearest slide (a copy at either end glides on,
    // then swaps for the slide it copies). A drag past a fifth of the strip
    // that is still nearest where it began carries on to the next one.
    const land = (from: number, d: number) => {
      const left = posOf(show);
      let el = slides[0];
      let i = 0;
      let dist = Infinity;
      const near = (c: HTMLElement | null, ci: number) => {
        if (!c) return;
        const cd = Math.abs(at(c) - left);
        if (cd < dist) { dist = cd; el = c; i = ci; }
      };
      slides.forEach((c, ci) => near(c, ci));
      near(show.clone, 0);
      near(show.lead, n - 1);
      const size = vertical ? scroller.clientHeight : scroller.clientWidth;
      if (i === from && Math.abs(d) > size / 5) { goTo(from + (d < 0 ? 1 : -1)); return; }
      schedule();
      show.index = i;
      paintDots();
      paintPlaces(show, el);
      const copy = el === show.clone || el === show.lead;
      moveTo(at(el), copy ? () => jumpTo(slides[i]) : undefined);
    };
    const end = (e: PointerEvent) => {
      if (!drag) return;
      const { moved, from, d } = drag;
      drag = null;
      swallow = moved;
      if (!moved) return;
      if (typeof scroller.hasPointerCapture === 'function' && scroller.hasPointerCapture(e.pointerId)) scroller.releasePointerCapture(e.pointerId);
      show.dragging = false;
      scroller.style.scrollBehavior = '';
      land(from, d);
      // A glide turns snap back on when it finishes; one that had nowhere to go doesn't.
      if (!show.animating) setSnap(true);
    };
    scroller.addEventListener('pointerup', end);
    scroller.addEventListener('pointercancel', end);
    // The browser would start dragging a card's picture a few pixels in and
    // cancel the pointer; a drag is not a click on the card it ended over.
    scroller.addEventListener('dragstart', (e) => e.preventDefault());
    scroller.addEventListener('click', (e) => { if (swallow) { swallow = false; e.preventDefault(); e.stopPropagation(); } }, true);
  }

  // Auto-play, and everything that holds it.
  const playing = () => auto && !show.userPaused && show.inView && !doc.hidden && !(pauseOnHover && show.hovered) && !show.focused && !show.pressed && root.isConnected;
  function schedule() {
    if (show.timer !== null) win.clearTimeout(show.timer);
    show.timer = null;
    if (!playing()) return;
    show.timer = win.setTimeout(() => {
      show.timer = null;
      if (!playing()) return;
      goTo(show.index + 1);
    }, everyMs);
  }
  root.addEventListener('pointerenter', () => { show.hovered = true; schedule(); });
  root.addEventListener('pointerleave', () => { show.hovered = false; schedule(); });
  root.addEventListener('focusin', () => { show.focused = true; schedule(); });
  root.addEventListener('focusout', () => { show.focused = !!root.matches(':focus-within'); schedule(); });
  scroller.addEventListener('pointerdown', (e) => {
    show.pressed = true;
    // On a touch screen a tap or a swipe stops auto-play for good: there is no
    // hover to hold it, and the pause button only shows to the keyboard.
    if (auto && e.pointerType === 'touch' && !show.userPaused) { show.userPaused = true; paintPause(); }
    cancelMove();
    schedule();
  }, { passive: true });
  const release = () => { if (show.pressed) { show.pressed = false; schedule(); } };
  win.addEventListener('pointerup', release, { passive: true });
  win.addEventListener('pointercancel', release, { passive: true });
  doc.addEventListener('visibilitychange', () => schedule());
  const IO = (win as Window & typeof globalThis).IntersectionObserver;
  if (typeof IO === 'function') {
    new IO((entries) => {
      for (const en of entries) show.inView = en.isIntersecting;
      schedule();
    }).observe(root);
  }
  // A resize moves the slides: land on the current one again, instantly.
  let resizeTimer: number | null = null;
  win.addEventListener('resize', () => {
    if (resizeTimer !== null) win.clearTimeout(resizeTimer);
    resizeTimer = win.setTimeout(() => {
      resizeTimer = null;
      cancelMove();
      if (ring) layRing(show, win);
      else fitEnds(show, win);
      jumpTo(slides[show.index]);
      fitArrows();
    }, 100);
  }, { passive: true });

  schedule();
  return show;
}

/** Bind every slide show on the page not yet bound. */
export function bindSlideShows(doc: Document, win: Window): void {
  doc.querySelectorAll<HTMLElement>('[data-pb-slides]').forEach((root) => {
    if (!shows.has(root)) bindSlideShow(root, win);
  });
}
